import { useMutation, useQuery } from '@apollo/client';
import _ from 'lodash';
import { useState } from 'react';
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Nav from 'react-bootstrap/Nav';
import Row from 'react-bootstrap/Row';
import { LinkContainer } from 'react-router-bootstrap';
import { Redirect, Route, Switch, useHistory, useRouteMatch } from 'react-router-dom';
import { LoadingLogo } from '../../../../devtools/components/Modal';
import { CREATE_VANITY_PIECE, GET_VANITY_PIECES } from '../../../components/apolloQueries';
import ItemSearchBox from '../components/ItemSearchBox';
import { filterItemsBySearch } from '../components/itemSearch';
import VanityPieceView from '../components/VanityPieceView';
import { CreateVanityPieceMutation, GetVanityPiecesQuery, VanityPieceType } from '../../../../../../__gqltypes__';
import { VANITY_PIECE_TYPE_LABELS } from '../utils';

const VanityPiecesView = () => {
  const match = useRouteMatch();
  const history = useHistory();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState<VanityPieceType | ''>('');
  const [search, setSearch] = useState('');
  const { data, loading, refetch } = useQuery<GetVanityPiecesQuery>(GET_VANITY_PIECES, { fetchPolicy: 'no-cache' });
  const [createVanityPiece, { loading: createLoading }] = useMutation<CreateVanityPieceMutation>(CREATE_VANITY_PIECE);

  const vanityPieces = data?.liveOps?.vanityPieces ?? [];
  const vanityPiecesById = _.keyBy(vanityPieces, 'id');
  const sortedPieces = _.sortBy(vanityPieces, ['name']);
  const visiblePieces = filterItemsBySearch(sortedPieces, search);

  const handleCreate = () => {
    if (!newName.trim().length) {
      alert('Please, enter valid name');
      return;
    }
    if (!newType) {
      alert('Please, select the vanity type');
      return;
    }
    createVanityPiece({ variables: { input: { name: newName.trim(), type: newType } } })
      .then(({ data: created }) => {
        setShowCreateModal(false);
        setNewName('');
        setNewType('');
        refetch().then(() => history.push(`/sp3/liveops/items/vanityPieces/${created?.createVanityPiece.id}`));
      })
      .catch(() => alert('Something went wrong :('));
  };

  return (
    <>
      <LoadingLogo show={loading || createLoading} />
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Add a new vanity piece</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col xs={3}>Name</Col>
            <Col xs={6}>
              <Form.Control required type="text" value={newName} onChange={(e) => setNewName(e.target.value)} />
            </Col>
          </Row>
          <Row className="mt-3">
            <Col xs={3}>Vanity type</Col>
            <Col xs={6}>
              <Form.Control
                as="select"
                value={newType}
                onChange={(e) => setNewType(e.target.value as VanityPieceType)}
              >
                <option value="" disabled>
                  Select...
                </option>
                {Object.values(VanityPieceType).map((type) => (
                  <option key={type} value={type}>
                    {VANITY_PIECE_TYPE_LABELS[type]}
                  </option>
                ))}
              </Form.Control>
            </Col>
          </Row>
          <Row className="mt-3 pl-1">
            <Button disabled={createLoading} onClick={handleCreate}>
              Add
            </Button>
          </Row>
        </Modal.Body>
      </Modal>
      {data && (
        <Row className="d-flex">
          <Col>
            <h1 className="text-center">Vanity Pieces</h1>
            <Button className="w-100 mb-3" variant="success" onClick={() => setShowCreateModal(true)}>
              Add a new vanity piece
            </Button>
            <ItemSearchBox
              value={search}
              onChange={setSearch}
              matchCount={visiblePieces.length}
              total={sortedPieces.length}
            />
            <Nav variant="pills" className="mb-3" style={{ maxHeight: '75vh', overflowY: 'auto' }}>
              {visiblePieces.map((piece) => (
                <Nav.Item key={piece.id} style={{ width: '100%' }} className="text-center">
                  <LinkContainer to={`${match.path}/${piece.id}`}>
                    <Nav.Link>
                      {piece.name}
                      <small className="d-block">{piece.type ? VANITY_PIECE_TYPE_LABELS[piece.type] : 'No type'}</small>
                    </Nav.Link>
                  </LinkContainer>
                </Nav.Item>
              ))}
            </Nav>
          </Col>
          <Col className="col-10">
            {sortedPieces.length === 0 && (
              <p className="text-center text-muted mt-5">
                No vanity pieces yet. Use &quot;Add a new vanity piece&quot; to create the first one.
              </p>
            )}
            <Switch>
              <Route
                path={`${match.path}/:vanityPieceId`}
                render={({ match: _match }) => {
                  const piece = vanityPiecesById[_match.params.vanityPieceId];
                  return piece ? <VanityPieceView vanityPiece={piece} refetch={refetch} /> : null;
                }}
              />
              {sortedPieces.length > 0 && <Redirect to={`${match.path}/${sortedPieces[0].id}`} />}
            </Switch>
          </Col>
        </Row>
      )}
    </>
  );
};

export default VanityPiecesView;
